'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import FBPixelScript from '@components/Analytics/Facebook/FBPixelScript';
import { pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, Input } from '@components/Form';
import { fetchApi, login } from '@utils/api/client';

const schema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  password: z.string().min(8),
  repeatPassword: z.string().min(8),
});

interface CompleteRegistrationFormProps {
  email: string;
  className?: string;
  onSuccess?: () => void;
}

export default function CompleteRegistrationForm({
  email,
  onSuccess,
  className,
}: CompleteRegistrationFormProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const locale = useLocale();
  const t = useTranslations('auth');

  const onSubmit = handleSubmit(async (data) => {
    if (data.password !== data.repeatPassword) {
      setError('repeatPassword', { message: t('completeRegistration.errors.passwordsDontMatch') });
      return;
    }

    try {
      await fetchApi('/users/update', {
        method: 'PATCH',
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          status: 'active',
        }),
      });

      const response = await login(email, data.password);

      if (!response.success) {
        setError('password', { message: t('completeRegistration.errors.error') });
        return;
      }

      pushSimpleGAEvent('transinfo_old_register_step_3', {
        transinfo_is_logged_in: 'true',
        transinfo_is_newsletter_user: 'false',
        transinfo_user_email: email,
        transinfo_user_provider: 'wordpress',
        locale: locale
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setError('password', { message: t('completeRegistration.errors.error') });
    }
  });

  return (
    <>
      <FBPixelScript event='CompleteRegistration' values={
        {
          content_name: 'new-user-registered',
          transinfo_registered_email: email
        }
      } />
      <form onSubmit={onSubmit} className={className}>
        <h2 className="text-md mb-3 font-bold text-premium-900 lg:mb-4 lg:text-xl dark:text-white">
          {t('completeRegistration.title')}
        </h2>

        <div className="mb-2 lg:mb-3">
          <Input
            className="mb-1"
            type="text"
            placeholder={t('completeRegistration.firstName')}
            error={!!errors.firstName}
            {...register('firstName')}
          />
          {errors.firstName && (
            <p className="text-sm text-red-500">{errors.firstName.message}</p>
          )}
        </div>
        <div className="mb-2 lg:mb-3">
          <Input
            className="mb-1"
            type="lastName"
            placeholder={t('completeRegistration.lastName')}
            error={!!errors.lastName}
            {...register('lastName')}
          />
          {errors.lastName && (
            <p className="text-sm text-red-500">{errors.lastName.message}</p>
          )}
        </div>
        <div className="mb-2 lg:mb-3">
          <Input
            className="mb-1"
            type="password"
            placeholder={t('completeRegistration.password')}
            error={!!errors.password}
            {...register('password')}
          />
          {errors.password && (
            <p className="text-sm text-red-500">{errors.password.message}</p>
          )}
        </div>
        <div className="mb-2 lg:mb-3">
          <Input
            className="mb-1"
            type="password"
            placeholder={t('completeRegistration.repeatPassword')}
            error={!!errors.repeatPassword}
            {...register('repeatPassword')}
          />
          {errors.repeatPassword && (
            <p className="text-sm text-red-500">
              {errors.repeatPassword.message}
            </p>
          )}
        </div>

        <div className="flex flex-col justify-center items-center">
          <Button
            id="register-complete"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('label.finish')}
          </Button>
        </div>
      </form>
    </>
  );
}
